import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiMail, FiCheck, FiAlertCircle } = FiIcons;

const RefundPage = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-20">
      <SEO title="Refund Policy" description="NinjaCopia 30-day money-back guarantee and refund policy details." />
      <section>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">
              Refund Policy
            </h1>

            <div className="text-sm text-gray-500 dark:text-gray-400 mb-8">
              Last updated: January 2025
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mb-8">
              <div className="flex items-center mb-3">
                <SafeIcon icon={FiCheck} className="w-6 h-6 text-green-600 dark:text-green-400 mr-3" />
                <h2 className="text-xl font-bold text-green-800 dark:text-green-200">
                  30-Day Money-Back Guarantee
                </h2>
              </div>
              <p className="text-green-700 dark:text-green-300">
                We stand behind NinjaCopia with a full 30-day money-back guarantee. If you're not completely satisfied with the software, we'll refund your purchase—no questions asked.
              </p>
            </div>

            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                  <SafeIcon icon={FiClock} className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-3" />
                  Refund Eligibility
                </h2>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                  <ul className="space-y-3 text-gray-600 dark:text-gray-400">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 shrink-0"></div>
                      <span>Refund requests must be made within 30 days of your original purchase date</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 shrink-0"></div>
                      <span>Applies to all NinjaCopia license types (Single, Two, or Three Machine licenses)</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 shrink-0"></div>
                      <span>No specific reason required—we respect your decision</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 shrink-0"></div>
                      <span>Full purchase price refunded, including any applicable taxes</span>
                    </li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">How to Request a Refund</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">1</span>
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Contact Support</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Email us at support@ninjacopia.com with your refund request
                    </p>
                  </div>

                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">2</span>
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Provide Details</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Include your order number and the email used for purchase
                    </p>
                  </div>

                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">3</span>
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Get Refunded</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Receive your full refund within 5-10 business days
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Refund Processing</h2>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <SafeIcon icon={FiClock} className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Processing Time</h4>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          Refunds are processed within 24 hours of approval. Funds typically appear in your account within 5-10 business days, depending on your payment method.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <SafeIcon icon={FiMail} className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Confirmation</h4>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          You'll receive an email confirmation once your refund has been processed, including the expected timeline for funds to appear in your account.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">License Deactivation</h2>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
                  <div className="flex items-start">
                    <SafeIcon icon={FiAlertCircle} className="w-6 h-6 text-yellow-600 dark:text-yellow-400 mr-3 mt-0.5 shrink-0" />
                    <div>
                      <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                        Important Note About License Access
                      </h4>
                      <p className="text-yellow-700 dark:text-yellow-300 text-sm mb-3">
                        Upon refund approval, your NinjaCopia license will be automatically deactivated. The software will stop functioning, and you will no longer have access to updates or support.
                      </p>
                      <p className="text-yellow-700 dark:text-yellow-300 text-sm">
                        Please ensure you've backed up any configuration settings you might want to preserve before requesting a refund.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Contact Information</h2>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
                  <p className="text-blue-800 dark:text-blue-200 mb-3">
                    For refund requests or questions about our refund policy:
                  </p>
                  <div className="flex items-center">
                    <SafeIcon icon={FiMail} className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
                    <span className="text-blue-700 dark:text-blue-300 font-medium">
                      support@ninjacopia.com
                    </span>
                  </div>
                  <p className="text-blue-600 dark:text-blue-400 text-sm mt-3">
                    We typically respond to refund requests within 24 hours during business days.
                  </p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RefundPage;